import { FilterOptionDto } from '@act/shared/data-transfer-objects';
import { Autocomplete, InputAdornment, TextField } from '@mui/material';
import { Fragment } from 'react';
import { Controller, Path } from 'react-hook-form';
import { BaseControlledInputProps } from '../BasicControlledInputProps';
import FormFieldWrappers from '../FormFieldWrappers';
import FormErrorHelper from '../SharedComponents/FormErrorHelper';
import { parseError } from '../form-utils/errorParser';
import { useFormTracking, useFormOptionTracking } from '../hooks';

type AutocompleteProps = {
  /**
   * The options to display in the autocomplete
   */
  options: FilterOptionDto[];
  /**
   * A start adornment for the autocomplete; an icon or React node
   */
  startAdornment?: JSX.Element;
  /**
   * Whether the autocomplete supports multiple selections
   */
  multiple?: boolean;
  /**
   * The maximum number of tags to display, -1 for no limit
   */
  limitTags?: number;
  isOptionEqualToValue?: (option, value) => boolean;
  /**
   * Defines how an option label is gotten and displayed
   *
   * @param TFieldValue - the option object with the label in it
   * @returns the label for the option
   */
  getOptionLabel: (TFieldValue) => string;
  /**
   * Style properties
   */
  style?: React.CSSProperties;
  groupBy?: (FilterOptionDto) => string;
  /**
   * Placeholder text for the input field
   */
  placeholder?: string;
};

export type ControlledAutocompleteProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = BaseControlledInputProps<FORM_TYPE, FIELD_NAME> & AutocompleteProps;

export const ControlledAutocomplete = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  name,
  label,
  control,
  options,
  disabled = false,
  multiple = false,
  groupBy,
  isOptionEqualToValue = (option, value) => {
    return option === value;
  },
  limitTags = -1,
  errors = undefined,
  startAdornment = undefined,
  style,
  getOptionLabel,
  trackingName,
  sensitive = true,
  placeholder,
}: ControlledAutocompleteProps<FORM_TYPE, FIELD_NAME>) => {
  // Auto-generate tracking props if trackingName is provided
  const trackingProps = useFormTracking(trackingName);
  const { generateOptionTracking } = useFormOptionTracking(
    trackingName,
    sensitive,
    options
  );

  return (
    <FormFieldWrappers.FieldWrapper>
      <FormFieldWrappers.InputWrapper>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            const handleChange = (
              _,
              data: FilterOptionDto[] | FilterOptionDto
            ) => {
              multiple
                ? onChange((data as FilterOptionDto[]).map(item => item.value))
                : onChange((data as FilterOptionDto)?.value);
            };
            const selectedValue = multiple
              ? options.filter(
                  option => Array.isArray(value) && value.includes(option.value)
                )
              : options.find(option => option.value === value);
            return (
              <Autocomplete
                {...trackingProps}
                style={style}
                defaultValue={[]}
                value={selectedValue}
                options={options}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                onChange={handleChange}
                multiple={multiple}
                limitTags={limitTags}
                groupBy={groupBy}
                disabled={disabled}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    {...generateOptionTracking(
                      (option as FilterOptionDto).value,
                      (option as FilterOptionDto).label
                    )}
                  >
                    {(option as FilterOptionDto).label}
                  </li>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    placeholder={placeholder}
                    error={!!error}
                    helperText={error?.message}
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '16px',
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <Fragment>
                          {startAdornment ? (
                            <InputAdornment
                              position="start"
                              sx={{ marginLeft: '4px' }}
                            >
                              {startAdornment}
                              {params.InputProps.startAdornment}
                            </InputAdornment>
                          ) : (
                            <Fragment>
                              {params.InputProps.startAdornment}
                            </Fragment>
                          )}
                        </Fragment>
                      ),
                    }}
                  />
                )}
              />
            );
          }}
        />
      </FormFieldWrappers.InputWrapper>
      {errors && parseError(name, errors) && (
        <FormErrorHelper name={name} errors={errors} />
      )}
    </FormFieldWrappers.FieldWrapper>
  );
};
